





































































































































import { AxiosError } from 'axios';
import moment from 'moment-timezone';
import Vue, { PropType } from 'vue';

import {
  WorkflowFormFieldTypeEnum,
  IWorkflowFormFieldData,
  IWorkflowFormFieldOptionSubmissionData,
  IWorkflowFormFieldSubmissionData,
  IWorkflowFormSectionData,
  IWorkflowFormSectionFieldData,
  IWorkflowFormSectionSubmissionData,
  IWorkflowFormSectionSubmissionFieldErrorData,
  WorkflowConstraintActionEnum,
  WorkflowFormFieldSubmission,
  IWorkflowFormFieldOptionData,
  WorkflowFormFieldSubmissionAttachment,
  IAttachmentData,
  WorkflowFormSectionFieldConstraint,
  IWorkflowFormSectionFieldConstraintData
} from '@/models';

import mixins from '@/utils/mixins';
import auth from '@/mixins/auth';
import snackbar from '@/mixins/snackbar';

import {
  convertDataURlToFile,
  createFile,
  downloadDataUrl,
  isValidDataUrl,
  setErrorFromAxiosError
} from '@/utils/helpers';
import { numericValidator } from '@/utils/validators';

import DatePicker from '../common/DatePicker.vue';
import DragDropFileInput from '../common/DragDropFileInput.vue';
import WorkflowFormSubmissionCalculatedField from './WorkflowFormSubmissionCalculatedField.vue';
import WorkflowFormSubmissionField from './WorkflowFormSubmissionField.vue';

export interface IFieldAttributes {
  disabled: boolean;
  required: boolean;
  readonly: boolean;
}

const MAX_TEMPLATE_ROWS = 100; // Define reasonable limit

export default mixins(auth, snackbar).extend({
  name: 'WorkflowFormSubmissionFields',

  components: {
    DatePicker,
    DragDropFileInput,
    WorkflowFormSubmissionCalculatedField,
    WorkflowFormSubmissionField
  },

  props: {
    fieldErrors: {
      type: Array as PropType<IWorkflowFormSectionSubmissionFieldErrorData[]>
    },
    answers: {
      type: Array as PropType<IWorkflowFormSectionSubmissionData[]>
    },
    sectionIndex: Number,
    section: Object as PropType<IWorkflowFormSectionData>,
    isReadonly: Boolean
  },

  data() {
    return {
      WorkflowFormFieldTypeEnum,
      WorkflowConstraintActionEnum
    };
  },

  computed: {
    isMobile(): boolean {
      return this.$vuetify.breakpoint.mobile;
    }
  },

  methods: {
    isValidDataUrl,
    numericValidator,

    // Newly added helpers for template matrix rendering
    isTemplateField(sectionField: IWorkflowFormSectionFieldData): boolean {
      const fieldTemplate = sectionField.field_template as IWorkflowFormFieldData;
      return (
        fieldTemplate &&
        fieldTemplate.type === WorkflowFormFieldTypeEnum.TemplateField
      );
    },

    isRemoveRowDisabled(
      templateField: IWorkflowFormSectionFieldData,
      rowIndex: number
    ): boolean {
      // disable removal if only one row remains (rowIndex 0 and only one row total)
      const rows = this.getNumberOfTemplateRows(templateField);
      return rowIndex === 0 && rows === 1;
    },

    genTemplateGridStyle(templateField: IWorkflowFormSectionFieldData) {
      const columns =
        this.getTemplateChildren(this.section, templateField).length +
        (this.isReadonly ? 0 : 1);
      return {
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`
      };
    },

    numericFieldValidator(v: string): boolean | string {
      if (this.isReadonly) {
        return true;
      }

      // Allow empty string for optional fields
      if (v === '') {
        return true;
      }

      // Validate non-empty values
      return this.numericValidator(v);
    },

    clearAllErrors(): void {
      this.$emit('clearAllErrors');
    },

    isConstraintSatisfied(
      constraint: IWorkflowFormSectionFieldConstraintData,
      fieldSubmissions: IWorkflowFormSectionSubmissionData[],
      templateIndex?: number
    ) {
      const constraintInstance = new WorkflowFormSectionFieldConstraint(
        constraint
      );

      const answers = fieldSubmissions
        .flatMap(ans => ans.fields!)
        .map(answer => new WorkflowFormFieldSubmission(answer));

      // Find if any constraint is satisfied
      return constraintInstance.isSatisfied(answers, templateIndex);
    },

    checkHiddenConstraintMet(
      sectionField: IWorkflowFormSectionFieldData,
      templateIndex?: number
    ) {
      let showField = true;

      if (sectionField.hidden) {
        showField = false;
      }

      if (sectionField.constraints.length > 0) {
        // Goes through list of constraints and check for hidden/visible actions
        sectionField.constraints.forEach(constraint => {
          const constraintConditionsMet = this.isConstraintSatisfied(
            constraint,
            this.answers,
            templateIndex
          );

          if (constraintConditionsMet) {
            switch (constraint.action) {
              case WorkflowConstraintActionEnum.Hidden:
                showField = false;
                break;

              case WorkflowConstraintActionEnum.Visible:
                showField = true;

                break;
              default:
                break;
            }
          }
        });
      }
      return showField;
    },

    genWorkflowFormFieldProperties(
      sectionField: IWorkflowFormSectionFieldData,
      fieldIndex = -1
    ): IFieldAttributes {
      let actionAttributes = {
        disabled: sectionField.disabled,
        required: sectionField.required,
        readonly: this.isReadonly
      };

      if (sectionField.constraints.length > 0) {
        //goes through list of constraints
        sectionField.constraints.forEach(constraint => {
          //find if all constraint’s target option is selected
          const constraintConditionsMet = this.isConstraintSatisfied(
            constraint,
            this.answers
          );

          if (constraintConditionsMet) {
            switch (constraint.action) {
              case WorkflowConstraintActionEnum.Required:
                actionAttributes.disabled = false;
                actionAttributes.required = true;

                break;

              case WorkflowConstraintActionEnum.Optional:
              case WorkflowConstraintActionEnum.Hidden:
                actionAttributes.disabled = false;
                actionAttributes.required = false;

                break;

              case WorkflowConstraintActionEnum.Disabled:
                actionAttributes.disabled = true;
                actionAttributes.required = false;

                break;

              default:
                break;
            }
          }
        });
      }

      const sectionFieldAnswer = this.answers[this.sectionIndex].fields[
        fieldIndex
      ];

      //checks if the target field has been answered and is set to disabled
      this.updateDisabledFieldAnswer(
        actionAttributes.disabled,
        sectionFieldAnswer,
        fieldIndex
      );

      return actionAttributes;
    },

    updateDisabledFieldAnswer(
      isDisabled: boolean,
      sectionFieldAnswer: IWorkflowFormFieldSubmissionData,
      fieldIndex = -1
    ) {
      if (
        isDisabled &&
        fieldIndex != -1 &&
        (sectionFieldAnswer.value ||
          sectionFieldAnswer.option_submissions.length > 0)
      ) {
        //clears submission answer when disabled
        const submission = new WorkflowFormFieldSubmission({
          field: sectionFieldAnswer.field,
          id: sectionFieldAnswer.id,
          value: '',
          option_submissions: []
        });

        this.answers[this.sectionIndex].fields.splice(
          fieldIndex,
          1,
          submission
        );
      }
    },

    getFieldError(
      fieldErrors: IWorkflowFormSectionSubmissionFieldErrorData[],
      sectionIndex: number,
      errorFieldIndex: number
    ): string {
      return fieldErrors[sectionIndex]
        ? fieldErrors[sectionIndex].errors[errorFieldIndex]
        : '';
    },

    genOptionItems(
      field: IWorkflowFormFieldData
    ): IWorkflowFormFieldOptionData[] {
      return field.options!.map(data => {
        return { ...data, option: data.id };
      });
    },

    genRadioBtnOptionsValue(
      radio: IWorkflowFormFieldSubmissionData,
      submissons: IWorkflowFormFieldOptionSubmissionData[]
    ): IWorkflowFormFieldOptionSubmissionData {
      let value = submissons.find(submisson => submisson.option == radio.id);

      if (!value) {
        value = {
          id: undefined,
          option: radio.id
        } as IWorkflowFormFieldOptionSubmissionData;
      }

      return value;
    },

    genInputId(section_field: IWorkflowFormSectionFieldData) {
      return `input-${section_field.id}`;
    },

    genLabel(section_field: IWorkflowFormSectionFieldData) {
      const attributes = this.genWorkflowFormFieldProperties(section_field);
      let label = (section_field.field_template as IWorkflowFormFieldData)
        .label;
      if (attributes.required) {
        label = `${label}*`;
      }
      return label;
    },

    genHint(section_field: IWorkflowFormSectionFieldData) {
      return (section_field.field_template as IWorkflowFormFieldData).hint;
    },

    downloadFileAttachment(
      base64: string,
      section_field: IWorkflowFormSectionFieldData
    ) {
      downloadDataUrl(
        base64,
        (section_field.field_template as IWorkflowFormFieldData).label
      );
    },

    genFileFieldValue(sectionIndex: number, fieldIndex: number) {
      return this.answers[sectionIndex].fields[fieldIndex].value
        ? convertDataURlToFile(
            this.answers[sectionIndex].fields[fieldIndex].value,
            'Attachment'
          )
        : null;
    },

    genMultiFileFieldValue(sectionIndex: number, fieldIndex: number) {
      return this.answers[sectionIndex].fields[fieldIndex].attachments
        ?.filter(attachment => attachment.file instanceof File)
        .map(attachment => attachment.file);
    },

    async addWorkflowFormFieldAttachments(
      files: File[],
      sectionIndex: number,
      fieldIndex: number
    ) {
      this.clearAllErrors();

      const attachments = files.map(
        file => new WorkflowFormFieldSubmissionAttachment({ file })
      );

      Vue.set(
        this.answers[sectionIndex].fields[fieldIndex],
        'attachments',
        attachments
      );
    },

    async downloadFileFromAttachment(attachment: IAttachmentData) {
      try {
        const data =
          typeof attachment.file === 'string'
            ? await this.$services.fileLoader.downloadFile(
                attachment.file as string
              )
            : attachment.file!;
        const fileName = attachment.filename || 'Attachment';
        createFile(data, fileName);

        this.createSnack({
          color: 'success darken-1',
          type: 'success',
          right: true,
          useBtn: false,
          msg: `File attachment download successfully`,
          btnText: 'Ok',
          timeout: 6000
        });
      } catch (err) {
        const error = setErrorFromAxiosError(err as AxiosError);
        this.createSnack({
          color: 'error darken-1',
          type: 'error',
          right: true,
          useBtn: false,
          msg: `File attachment download failed: ${error.message}`,
          btnText: 'Ok',
          timeout: 6000
        });
      }
    },

    removeFileFromAttachment(
      sectionIndex: number,
      fieldIndex: number,
      attachment: IAttachmentData
    ) {
      const attachments =
        this.answers[sectionIndex].fields[fieldIndex].attachments ?? [];

      const indexOfAttachment = attachments.indexOf(attachment);

      attachments.splice(indexOfAttachment, 1);

      Vue.set(
        this.answers[sectionIndex].fields[fieldIndex],
        'attachments',
        attachments
      );
    },

    async addWorkflowFormAttachment(
      file: File | null,
      sectionIndex: number,
      fieldIndex: number
    ) {
      this.clearAllErrors();
      let base64 = '';

      if (file) {
        base64 = await this.getBase64(file);
      }
      Vue.set(this.answers[sectionIndex].fields[fieldIndex], 'value', base64);
    },

    getBase64(file: File) {
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result?.toString() || '');
        reader.onerror = error => reject(error);
      });
    },

    onDateFieldInput(
      date: string,
      sectionIndex: number,
      fieldIndex: number,
      urgencyThreshold: number
    ) {
      this.clearAllErrors();

      this.answers[sectionIndex].fields[fieldIndex].value = date;

      if (urgencyThreshold > 0) {
        let isDateInThreshold = false;

        if (date) {
          const dateValue = moment(date);

          isDateInThreshold = dateValue.isBetween(
            moment(new Date()).startOf('day'),
            moment(new Date())
              .add(urgencyThreshold, 'days')
              .endOf('day'),
            'hours',
            '[]'
          );
        }

        Vue.set(
          this.answers[sectionIndex].fields[fieldIndex],
          'is_urgent',
          isDateInThreshold
        );
      }
    },

    // ---------------- Template Field Helpers ----------------
    // Extracts the template pointer from a field (adjust the property name to your schema)
    getTemplatePointer(field: IWorkflowFormSectionFieldData): number | null {
      return (
        ((field.field_template as IWorkflowFormFieldData).template as number) ??
        null
      );
    },

    isChildField(field: IWorkflowFormSectionFieldData): boolean {
      return this.getTemplatePointer(field) != null;
    },

    getTemplateChildren(
      section: IWorkflowFormSectionData,
      templateField: IWorkflowFormSectionFieldData
    ): IWorkflowFormSectionFieldData[] {
      const templateIdCandidates = [
        (templateField.field_template as IWorkflowFormFieldData).id
      ].filter(Boolean);

      return (section.section_fields as IWorkflowFormSectionFieldData[]).filter(
        f => {
          const p = this.getTemplatePointer(f);
          return p != null && templateIdCandidates.includes(p);
        }
      );
    },

    getNumberOfTemplateRows(
      templateField: IWorkflowFormSectionFieldData
    ): number {
      const children = this.getTemplateChildren(this.section, templateField);

      // Determine highest template_index among existing answers inside the template
      const highestTemplateIndex = this.answers[this.sectionIndex].fields
        .filter(answer => {
          const fieldId =
            typeof answer.field === 'number' ? answer.field : answer.field.id;
          return children.some(child => child.id === fieldId);
        })
        .reduce((max, ans) => {
          const idx = ans.template_index ?? -1; // Ensure template_index is defined
          if (idx >= MAX_TEMPLATE_ROWS) {
            throw new Error(`Template index exceeds maximum allowed rows`);
          }
          return idx > max ? idx : max;
        }, -1);

      const numberOfRows = highestTemplateIndex + 1;

      return numberOfRows;
    },

    getMaxTemplateRow(templateField: IWorkflowFormSectionFieldData) {
      return this.getNumberOfTemplateRows(templateField);
    },

    // Finds the index of the answer corresponding to a given field (and optional template row)
    getAnswerIndex(
      field: IWorkflowFormSectionFieldData,
      rowIndex?: number
    ): number {
      const fieldId = field.id;
      const isTemplateChild = this.isChildField(field);

      // When dealing with template child rows
      if (isTemplateChild && typeof rowIndex === 'number') {
        return this.answers[this.sectionIndex].fields.findIndex(sub => {
          const subFieldId =
            typeof sub.field === 'number' ? sub.field : sub.field.id;
          return subFieldId === fieldId && sub.template_index === rowIndex;
        });
      }

      // Non-template fields (or no row specified): calculate index based on the field index
      return this.answers[this.sectionIndex].fields.findIndex(sub => {
        const subFieldId =
          typeof sub.field === 'number' ? sub.field : sub.field.id;
        return subFieldId === fieldId;
      });
    },

    createEmptySubmission(
      field: IWorkflowFormSectionFieldData,
      formIndex: number
    ): IWorkflowFormFieldSubmissionData {
      return ({
        id: undefined,
        field: field.id!,
        value: '',
        template_index: formIndex,
        option_submissions: [],
        attachments: []
      } as unknown) as IWorkflowFormFieldSubmissionData;
    },

    addNewTemplateRow(templateField: IWorkflowFormSectionFieldData) {
      const currentRow = this.getMaxTemplateRow(templateField);
      const children = this.getTemplateChildren(this.section, templateField);

      const newTemplateRow = children.map(child => {
        // Find the last existing row to clone structure from (if available)
        const lastExistingRow = currentRow > 0 ? currentRow - 1 : 0;

        const baseIdx = this.answers[this.sectionIndex].fields.findIndex(
          answer => {
            const fieldRef = answer.field;
            const fieldId =
              typeof fieldRef === 'number' ? fieldRef : fieldRef.id;

            return (
              answer.template_index === lastExistingRow && fieldId === child.id
            );
          }
        );

        // If a submission for this child at the target row already exists, skip to avoid duplicates
        const existingIdx = this.getAnswerIndex(child, currentRow);
        if (existingIdx !== -1) {
          return null;
        }

        if (baseIdx === -1) {
          // No base row found; fallback to a clean empty submission for this child
          return this.createEmptySubmission(child, currentRow);
        }

        const baseSubmission = this.answers[this.sectionIndex].fields[baseIdx];

        if (!baseSubmission) {
          return this.createEmptySubmission(child, currentRow);
        }

        return {
          id: undefined,
          field: baseSubmission.field,
          value: '',
          template_index: currentRow,
          option_submissions: [],
          attachments: []
        } as IWorkflowFormFieldSubmissionData;
      });

      /**
       * Checks whether the newTemplateRow array actually contains any usable entry.
       * Intended use: guard submission logic so it only runs when there is at least one meaningful row present.
       */
      if (
        newTemplateRow.length > 0 &&
        newTemplateRow.every(row => row !== null)
      ) {
        this.$emit('add-template-row', newTemplateRow);
      }

      this.clearAllErrors();
    },

    // Gets the index of a field in the answers for a specific display row (1-based in UI)
    getFieldIndexForRow(
      child: IWorkflowFormSectionFieldData,
      displayRow: number
    ): number {
      const targetTemplateIndex = displayRow - 1; // internal 0-based
      return this.answers[this.sectionIndex].fields.findIndex(field => {
        const fieldId =
          typeof field.field === 'number' ? field.field : field.field.id;
        return (
          field.template_index === targetTemplateIndex && fieldId === child.id
        );
      });
    },

    removeTemplateRow(
      templateField: IWorkflowFormSectionFieldData,
      rowIndex: number
    ) {
      if (rowIndex === 0) {
        // Keep at least one row (row 0) to avoid empty template set.
        // Instead of deleting, just clear values for row 0 children if it's the only row.
        const rows = this.getNumberOfTemplateRows(templateField);
        if (rows === 1) {
          this.getTemplateChildren(this.section, templateField).forEach(
            child => {
              const idx = this.getAnswerIndex(child, 0);
              if (idx !== -1) {
                // Reset value & option_submissions & attachments
                const sub: IWorkflowFormFieldSubmissionData = this.answers[
                  this.sectionIndex
                ].fields[idx];
                sub.value = '';
                sub.option_submissions = [];
                sub.attachments = [];
              }
            }
          );
          this.clearAllErrors();
          return;
        }
      }

      const children = this.getTemplateChildren(this.section, templateField);
      // Remove submissions matching this rowIndex for each child
      this.answers[this.sectionIndex].fields = this.answers[
        this.sectionIndex
      ].fields.filter(answer => {
        const fieldRef = answer.field;
        const fieldId = typeof fieldRef === 'number' ? fieldRef : fieldRef.id;
        const isChild = children.some(c => c.id === fieldId);
        if (!isChild) return true;
        // Keep if different row
        return answer.template_index !== rowIndex;
      });

      // Reindex rows above the removed one (shift template_index down by 1)
      this.answers[this.sectionIndex].fields.forEach(sub => {
        const fieldRef = sub.field;
        const fieldId = typeof fieldRef === 'number' ? fieldRef : fieldRef.id;
        if (
          children.some(c => c.id === fieldId) &&
          typeof sub.template_index === 'number' &&
          sub.template_index > rowIndex
        ) {
          sub.template_index = sub.template_index - 1;
        }
      });

      this.clearAllErrors();
    },

    genInputIdRow(
      section_field: IWorkflowFormSectionFieldData,
      rowIndex: number
    ) {
      return `input-${section_field.id}-row-${rowIndex}`;
    },

    getFieldErrorForField(
      field: IWorkflowFormSectionFieldData,
      rowIndex: number
    ): string {
      const idx = this.getFieldIndexForRow(field, rowIndex + 1);
      if (idx === -1) return '';
      return this.getFieldError(
        this.fieldErrors ||
          ([] as IWorkflowFormSectionSubmissionFieldErrorData[]),
        this.sectionIndex,
        idx
      );
    }
  }
});
